import { SdgUnitsMode } from '@upright/common/model/output/scores/sdg-alignment/types';
import { useQueryState, parseAsStringLiteral } from 'nuqs';
import { loadSdgPureRevenueShareMode, saveSdgPureRevenueShareMode } from '@upright/ui/common/generic/utilities/storage';

/**
 * This hook is used to manage the SDG units mode in the URL.
 * It also saves the chosen units mode to local storage.
 * */
export function useSdgUnitsMode() {
  const defaultValue = loadSdgPureRevenueShareMode()
    ? SdgUnitsMode.PURE_REVENUE_SHARES
    : SdgUnitsMode.SUMMARY_PERCENTAGE;

  const allowedUrlValues = Object.values(UI_MODE_TO_URL_MODE_MAP);

  const [unitsModeInUrl, setUnitsModeInUrl] = useQueryState(
    'sdg-units-mode',
    parseAsStringLiteral(allowedUrlValues).withDefault(UI_MODE_TO_URL_MODE_MAP[defaultValue]).withOptions({
      shallow: true,
      clearOnDefault: false,
    }),
  );

  const unitsMode = URL_MODE_TO_UI_MODE_MAP[unitsModeInUrl];

  const setUnitsMode = async (newMode: SdgUnitsMode) => {
    saveSdgPureRevenueShareMode(newMode === SdgUnitsMode.PURE_REVENUE_SHARES);
    await setUnitsModeInUrl(UI_MODE_TO_URL_MODE_MAP[newMode]);
  };

  return [unitsMode, setUnitsMode] as const;
}

/**
 * Mapping needed as to not modify the original @see SdgUnitsMode enum
 * as it is used for GTM events.
 * */
const UI_MODE_TO_URL_MODE_MAP = {
  [SdgUnitsMode.PURE_REVENUE_SHARES]: 'pure-revenue-shares',
  [SdgUnitsMode.SUMMARY_PERCENTAGE]: 'summary-percentage',
} as const;

const URL_MODE_TO_UI_MODE_MAP = {
  'pure-revenue-shares': SdgUnitsMode.PURE_REVENUE_SHARES,
  'summary-percentage': SdgUnitsMode.SUMMARY_PERCENTAGE,
} as const;
